import { definePreset } from '@primeuix/themes';
import Lara from '@primeuix/themes/lara';

// Keep these in sync with src/styles/_variables.scss.
const inputBorder = '#e9eaec';
const inputBackgroundDisabled = '#f3f3f3';
const inputLabel = '#aeb6bd';
const inputFocusShadow = '0 0 0 0.2rem rgb(0 123 255 / 25%)';
const blue = '#004cdd';
const greenAlternative = '#3dcc39';
const greenDark = '#008015';

/**
 * PrimeNG theme for Reedge. Based on Lara with the Reedge teal as primary color and the Bootstrap gray scale as
 * surface palette, so PrimeNG components match the Bootstrap based styling of the rest of the application.
 * Things tokens can't express (border widths, fixed heights, text transforms) live in src/styles/_overwrites/prime-ng-theme.scss.
 */
export const ReedgePreset = definePreset(Lara, {
  primitive: {
    borderRadius: {
      none: '0',
      xs: '2px',
      sm: '3px',
      md: '0.25rem',
      lg: '0.25rem',
      xl: '0.3rem',
    },
    teal: {
      50: '#e7f5f5',
      100: '#b9f3f0',
      200: '#8ceae6',
      300: '#5fe1db',
      400: '#33d8d0',
      500: '#00c7c1',
      600: '#00afa9',
      700: '#00928c',
      800: '#00756f',
      900: '#004c47',
      950: '#00312e',
    },
    bootstrapGray: {
      50: '#f8f9fa',
      100: '#e9ecef',
      200: '#dee2e6',
      300: '#ced4da',
      400: '#adb5bd',
      500: '#6c757d',
      600: '#5a6268',
      700: '#495057',
      800: '#343a40',
      900: '#212529',
      950: '#121416',
    },
  },
  semantic: {
    primary: {
      50: '{teal.50}',
      100: '{teal.100}',
      200: '{teal.200}',
      300: '{teal.300}',
      400: '{teal.400}',
      500: '{teal.500}',
      600: '{teal.600}',
      700: '{teal.700}',
      800: '{teal.800}',
      900: '{teal.900}',
      950: '{teal.950}',
    },
    formField: {
      paddingX: '1rem',
      paddingY: '0.8125rem',
      borderRadius: '{border.radius.md}',
      focusRing: {
        shadow: inputFocusShadow,
      },
    },
    list: {
      padding: '0',
      option: {
        padding: '0.3rem 1rem',
      },
      optionGroup: {
        padding: '0.5rem 1rem 0 1rem',
      },
    },
    overlay: {
      select: {
        shadow: '0 2px 4px 0 rgb(0 0 0 / 15%)',
      },
      modal: {
        borderRadius: '{border.radius.xl}',
      },
    },
    colorScheme: {
      light: {
        surface: {
          0: '#ffffff',
          50: '{bootstrapGray.50}',
          100: '{bootstrapGray.100}',
          200: '{bootstrapGray.200}',
          300: '{bootstrapGray.300}',
          400: '{bootstrapGray.400}',
          500: '{bootstrapGray.500}',
          600: '{bootstrapGray.600}',
          700: '{bootstrapGray.700}',
          800: '{bootstrapGray.800}',
          900: '{bootstrapGray.900}',
          950: '{bootstrapGray.950}',
        },
        primary: {
          color: '{primary.500}',
          contrastColor: '#ffffff',
          hoverColor: '#62d5d2',
          activeColor: '{primary.600}',
        },
        highlight: {
          background: '{primary.50}',
          focusBackground: '{primary.100}',
          color: '{text.color}',
          focusColor: '{text.color}',
        },
        formField: {
          borderColor: inputBorder,
          hoverBorderColor: inputBorder,
          focusBorderColor: inputBorder,
          disabledBackground: inputBackgroundDisabled,
          disabledColor: '{text.color}',
          placeholderColor: '{surface.700}',
        },
        list: {
          option: {
            focusBackground: '{surface.50}',
          },
        },
      },
    },
  },
  components: {
    select: {
      overlay: {
        borderColor: '{surface.200}',
      },
    },
    multiselect: {
      overlay: {
        borderColor: '{surface.200}',
      },
    },
    datatable: {
      headerCell: {
        padding: '0.5rem',
        selectedColor: blue,
      },
      columnTitle: {
        fontWeight: '600',
      },
      bodyCell: {
        padding: '0.5rem',
        borderColor: '{surface.100}',
      },
      row: {
        hoverBackground: '{surface.50}',
      },
      sortIcon: {
        color: inputLabel,
        hoverColor: '{text.muted.color}',
      },
      colorScheme: {
        light: {
          headerCell: {
            background: '{surface.0}',
            hoverBackground: '{surface.50}',
            color: inputLabel,
            selectedBackground: '{surface.50}',
          },
        },
      },
    },
    treetable: {
      headerCell: {
        padding: '1rem',
      },
      columnTitle: {
        fontWeight: '600',
      },
      bodyCell: {
        // Same vertical padding as table cells, so tree rows are as high as table rows.
        padding: '0.25rem 1rem',
      },
    },
    tabs: {
      // The bottom lines are drawn as box shadows in prime-ng-theme.scss; the border colors below are only used there.
      tablist: {
        borderWidth: '0',
        borderColor: '{surface.200}',
        background: 'transparent',
      },
      tab: {
        borderWidth: '0',
        borderColor: '{surface.200}',
        hoverBorderColor: '{surface.400}',
        activeBorderColor: '{primary.color}',
        color: '{text.muted.color}',
        hoverColor: '{text.muted.color}',
        activeColor: '{primary.color}',
        padding: '1rem',
        fontWeight: '600',
        margin: '0',
        focusRing: {
          shadow: 'none',
        },
      },
      tabpanel: {
        background: 'transparent',
        padding: '0',
      },
      colorScheme: {
        light: {
          tab: {
            background: '{surface.0}',
            hoverBackground: '{surface.0}',
            activeBackground: '{surface.0}',
          },
        },
      },
    },
    tooltip: {
      root: {
        padding: '0.5rem',
        borderRadius: '{border.radius.md}',
        shadow: '0 1px 6px 0 rgb(0 0 0 / 16%)',
      },
      colorScheme: {
        light: {
          root: {
            background: '#ffffff',
            color: '#000000',
          },
        },
      },
    },
    paginator: {
      root: {
        background: 'transparent',
      },
      navButton: {
        selectedBackground: '{primary.color}',
        selectedColor: '{primary.contrast.color}',
        color: '{surface.500}',
        hoverColor: '{surface.500}',
      },
    },
    checkbox: {
      root: {
        width: '22px',
        height: '22px',
        borderRadius: '4px',
        borderColor: '{surface.300}',
        checkedBackground: greenAlternative,
        checkedHoverBackground: greenAlternative,
        checkedBorderColor: greenAlternative,
        checkedHoverBorderColor: greenAlternative,
        checkedFocusBorderColor: greenAlternative,
        hoverBorderColor: greenAlternative,
        focusBorderColor: '{surface.300}',
      },
      icon: {
        size: '0.875rem',
      },
    },
    radiobutton: {
      root: {
        width: '1.25rem',
        height: '1.25rem',
        borderColor: '{surface.300}',
        hoverBorderColor: '{primary.color}',
        focusBorderColor: '{primary.color}',
      },
      icon: {
        size: '0.75rem',
      },
    },
    togglebutton: {
      root: {
        padding: '0.5rem 1rem',
        fontWeight: '400',
        borderColor: inputBorder,
        checkedBackground: '{primary.color}',
        checkedColor: '{primary.contrast.color}',
        checkedBorderColor: '{primary.color}',
      },
      icon: {
        checkedColor: '{primary.contrast.color}',
      },
    },
    toggleswitch: {
      colorScheme: {
        light: {
          root: {
            checkedHoverBackground: '{primary.color}',
          },
        },
      },
    },
    stepper: {
      separator: {
        activeBackground: greenDark,
      },
      stepNumber: {
        background: '{surface.200}',
        borderColor: 'transparent',
        color: '{surface.500}',
        fontSize: '1rem',
        fontWeight: '700',
        size: '2rem',
      },
      stepTitle: {
        color: '{text.muted.color}',
        activeColor: '{text.color}',
        fontWeight: '600',
      },
      steppanels: {
        padding: '1rem',
      },
    },
    dialog: {
      header: {
        padding: '1.5rem',
      },
    },
  },
});
